/**
 * "A new version is ready - Reload" (Idan, 14 Oct 2026).
 *
 * A deploy replaces the app's files. A tab opened before it still asks for the OLD names of the
 * parts it loads on demand (the 3D viewer, most pages) and gets a 404: webpack throws a
 * ChunkLoadError. Signing in does not reload the page, so a tab left open overnight hits it
 * (Nati, 14 Oct 2026: winery's 3D crashed with "Loading chunk 772 failed" after a 03:23 deploy;
 * it happened on every deploy day before).
 *
 * The app learns a new version is live in two ways:
 *  - a part fails to load (isChunkLoadError), caught by ViewerErrorBoundary;
 *  - checkForNewVersion(): the live page (index.html, never cached) names start-up files this tab
 *    did not load. Any change gives them new names: the map of on-demand files lives in them.
 * Either way markNewVersionReady() shows "A new version is ready - Reload". Nothing reloads by
 * itself: the person chooses when, so nothing unsaved is lost.
 */

/** A part of the app could not be loaded (webpack names it the same in every browser). */
export const isChunkLoadError = (error: unknown): boolean => {
  const e = error as { name?: unknown; message?: unknown } | null | undefined;
  if (!e) return false;
  return e.name === 'ChunkLoadError' || /Loading chunk [\w-]+ failed/i.test(String(e.message || ''));
};

let ready = false;
const listeners = new Set<() => void>();

export const isNewVersionReady = (): boolean => ready;

/** Shows "A new version is ready - Reload" wherever the app listens (once; it stays until a reload). */
export const markNewVersionReady = (): void => {
  if (ready) return;
  ready = true;
  listeners.forEach(listener => {
    try { listener(); } catch { /* one listener cannot stop the others */ }
  });
};

export const onNewVersionReady = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

// The app's own start-up files carry a content hash: /main-0819e42f.fdfd1ccee9085bcda339.js.
// Unhashed names (the dev server) never match, so nothing is compared there.
const APP_SCRIPT = /^\/(?:main|vendors|runtime)[\w.-]*\.[0-9a-f]{16,}\.js$/;

const pathOf = (src: string): string => {
  try { return new URL(src, 'https://x.invalid').pathname; } catch { return ''; }
};

/** The app's own start-up scripts a page names, from its HTML. */
export const appScriptNames = (html: string): string[] =>
  Array.from(String(html).matchAll(/<script\b[^>]*\bsrc=["']([^"']+)["']/gi))
    .map(match => pathOf(match[1]))
    .filter(path => APP_SCRIPT.test(path));

/** The app's own start-up scripts this tab loaded. */
export const loadedAppScriptNames = (doc: Document = document): string[] =>
  Array.from(doc.scripts)
    .map(script => pathOf(script.getAttribute('src') || ''))
    .filter(path => APP_SCRIPT.test(path));

/**
 * Asks the live page whether a newer version is deployed; marks it ready when the live page names a
 * start-up file this tab did not load. Answers false when it cannot tell (offline, dev server).
 */
export const checkForNewVersion = async (
  fetchPage: (url: string, init: RequestInit) => Promise<Response> = (url, init) => fetch(url, init),
  doc: Document = document
): Promise<boolean> => {
  if (ready) return true;
  const loaded = new Set(loadedAppScriptNames(doc));
  if (!loaded.size) return false;
  try {
    const response = await fetchPage('/', { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) return false;
    const live = appScriptNames(await response.text());
    if (!live.length) return false;
    const changed = live.some(name => !loaded.has(name));
    if (changed) markNewVersionReady();
    return changed;
  } catch {
    return false;
  }
};

/** For the unit tests only. */
export const resetNewVersionForTests = (): void => {
  ready = false;
  listeners.clear();
};
